import { FilterAlt, GridView, ViewList, ZoomIn, ZoomOut, Settings } from "@mui/icons-material";
import { Autocomplete, Grid, IconButton, InputAdornment, Slider, Stack, TextField, Tooltip, ToggleButtonGroup } from "@mui/material";

import TooltipButton, { TooltipButtonProps } from "components/Tooltip/TooltipButton";
import TooltipToggleButton from "components/Tooltip/TooltipToggleButton";
import ButtonGroup from '@mui/material/ButtonGroup';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { Document } from "types";

export type FileExplorerView = "table" | "gallery";

export type FileExplorerHeaderProps = {
    iconSize?: number,
    view?: FileExplorerView,
    tags?: string[],
    selection?: Document[],
    recyleBin?: boolean,
    upload?: boolean,
    edit?: boolean,
    canEditConfig?: boolean,
    configUrl?: string,
    deletion?: boolean,
    download?: boolean,
    isFilterBySelection?: boolean,
    onFilterBySelection?: () => void,
    onIconSizeChange?: (newSize: number) => void,
    onViewChange?: (newView: FileExplorerView) => void,
    onUpload?: () => void,
    onDelete?: () => void,
    onRestore?: () => void,
    onEdit?: () => void,
    onDownload?: () => void,
    onRecycleBinChange?: (recyleBinMode: boolean) => void,
    onTagFilter?: (filter: string[]) => void,
};

export function FileExplorerHeader({
    view,
    tags,
    selection = [],
    iconSize,
    recyleBin,
    upload,
    edit,
    deletion,
    download,
    canEditConfig,
    configUrl,
    isFilterBySelection,
    onFilterBySelection,
    onIconSizeChange,
    onViewChange,
    onUpload = () => { },
    onEdit = () => { },
    onDelete = () => { },
    onDownload = () => { },
    onRestore = () => { },
    onRecycleBinChange = () => { },
    onTagFilter = () => { }
}: FileExplorerHeaderProps) {

    return (
        <Grid container spacing={1}>
            <Grid item md={8} sm={12} >
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {upload && !recyleBin && <StyledTooltipButton
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            buttonId="upload"
                            onClick={() => onUpload()}
                        />}
                        <ButtonGroup variant="outlined">
                            {download && <StyledTooltipButton
                                startIcon={<DownloadIcon />}
                                buttonId="downloadAll"
                                disabled={selection.length === 0}
                                onClick={() => onDownload()}
                            />}
                            {deletion && recyleBin && <StyledTooltipButton
                                buttonId="restoreAll"
                                disabled={selection.length === 0}
                                onClick={() => onRestore()}
                            />}
                            {edit && !recyleBin && <StyledTooltipButton
                                startIcon={<EditIcon />}
                                buttonId="editAll"
                                disabled={selection.length === 0}
                                onClick={() => onEdit()}
                            />}
                            {deletion && <StyledTooltipButton
                                startIcon={<DeleteIcon />}
                                buttonId="deleteAll"
                                disabled={selection.length === 0}
                                onClick={() => onDelete()}
                            />}
                        </ButtonGroup>
                        <ToggleButtonGroup value={view || "table"} onChange={onViewToggleChange} exclusive size="small" >
                            <TooltipToggleButton groupId="explorerView" value="table" sx={{ padding: "5px" }} color="primary">
                                <ViewList sx={{ fontSize: 20 }} />
                            </TooltipToggleButton>
                            <TooltipToggleButton groupId="explorerView" value="gallery" sx={{ padding: "5px" }} color="primary">
                                <GridView sx={{ fontSize: 20 }} />
                            </TooltipToggleButton>
                        </ToggleButtonGroup>
                        {view === "gallery" && <Stack spacing={2} direction="row" alignItems="center" width="200px">
                            <ZoomOut sx={(theme) => ({ color: theme.palette.text.primary })} />
                            <Slider aria-label="Zoom" value={iconSize} onChange={handleIconSize} min={100} max={600} />
                            <ZoomIn sx={(theme) => ({ color: theme.palette.text.primary })} />
                        </Stack>}
                        <TooltipToggleButton
                            groupId="filterBySelectionToggle"
                            value="filterBySelection"
                            color="primary"
                            id="filterBySelection"
                            selected={isFilterBySelection}
                            onChange={onFilterBySelection}
                            size="small"
                            sx={{ padding: '5px', minWidth: 'auto', height: '32px' }}
                            disabled={selection.length === 0 && !isFilterBySelection}
                        >
                            <ChecklistIcon sx={{ fontSize: 20 }} />
                        </TooltipToggleButton>
                    </Stack>
                </Stack >
            </Grid>

            <Grid item md={4} sm={12} xs={12}>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                    {tags && <Autocomplete
                        fullWidth
                        aria-label="tags"
                        id="tags"
                        data-testid="tags"
                        multiple
                        size="small"
                        options={(tags || [])}
                        onChange={(_, newValue) => {
                            onTagFilter && onTagFilter(newValue);
                        }}
                        selectOnFocus
                        clearOnBlur
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <InputAdornment position="start">
                                                <FilterAlt />
                                            </InputAdornment>
                                            {params.InputProps.startAdornment}
                                        </>
                                    ),
                                }}
                                placeholder={"Filter Tags"}
                            />
                        )}
                    />}
                    {canEditConfig && <Tooltip title="Configuration">
                        <IconButton 
                            size="small" 
                            data-testid="tags-settings-icon"
                            onClick={() => {
                                const url = configUrl;
                                window.open(url, '_blank');
                            }}
                        >
                            <Settings />
                        </IconButton>
                    </Tooltip>}
                </Stack>
            </Grid>
        </Grid >
    );

    function handleIconSize(event: any, value: number | number[]) {
        onIconSizeChange(value as number);
    }

    function onViewToggleChange(event: any, newView: FileExplorerView) {
        if (!newView) return;
        onViewChange(newView);
    }

    function onRecyleBinClick() {
        onRecycleBinChange(!recyleBin)
    }
}

function StyledTooltipButton(props: TooltipButtonProps) {
    return <TooltipButton size="small" disableElevation {...props} />
}